import { Link } from "react-router-dom";
import { PILLARS } from "@/data/modules";
import { emojiDe } from "@/data/emojisDeFamilia.mjs";
import { colorDeLaCompetencia } from "@/lib/competencias.mjs";
import { pistasDe } from "@/lib/adelanta.mjs";

const TRAE_CURSO = () => import("@/pages/CursoDetail");

/**
 * LAS CINCO COMPETENCIAS, COMO PRIMERA MITAD DE LA FILA 2.
 *
 * DE DÓNDE SALE. Vivían dentro de `HeroLanding`: cinco píldoras grises de once píxeles,
 * debajo del subtítulo y por encima de un párrafo sobre el BOE. Gabri, 19 de octubre de 2026:
 * *«las competencias han perdido mucho protagonismo, deberían estar más vistosas abajo, o que
 * sean la segunda fila»*. Y tenía razón de sobra: son la forma en que esta casa organiza todo
 * lo demás —el mentor que te toca, el mapa de tu curso, el color de una ficha— y estaban
 * puestas como un pie de foto.
 *
 * Y ES LA MISMA TARJETA QUE LA DE `/cursos`, desde el 20 de octubre de 2026: *«copia el tipo
 * de tarjeta que ya tenemos en cursos: el encabezado de color va un poco más abajo, añade los
 * emojis de materia»*. La de allí —`FamiliasDeLaCompetencia`— ya tenía resueltas tres cosas
 * que aquí estaban a medias, y cada una hace un trabajo distinto:
 *
 *   · LA BANDA de ocho píxeles arriba, tenue en reposo y entera al pasar. Un filete de tres
 *     pegado al borde se lee como una raya de más; una banda se lee como el color de algo.
 *     **El color pleno es un momento, no un estado**, que es la regla de la casa.
 *   · EL EMOJI, grande y a la izquierda. No se inventa ninguno: los dieciocho los eligió
 *     Gabri familia por familia en `data/emojisDeFamilia.mjs`, y cada competencia dice en
 *     `PILLARS` de cuál toma el suyo. Un emoji escrito aquí sería el decimonoveno, elegido
 *     por nadie y fuera de la lista que vigila `prueba_emojis_familia.mjs`.
 *   · EL COLOR SALE DE `lib/competencias.mjs`, que es donde ya vivía. Había aquí un segundo
 *     mapa de cinco clases que decía lo mismo: dos listas de colores que nadie compara se
 *     separan el día que nazca la sexta competencia.
 *
 * SIGUEN SIENDO ENLACES, como ya lo eran: quien pulsa «Comunicación» espera abrir
 * Comunicación. El destino sale de `p.key`, que es el slug de su curso —lo mismo que usa
 * `/cursos`—, así que no hay una segunda tabla que se pueda desincronizar.
 */
export default function Competencias() {
  // YA NO ES UNA SECCION, Y ESO LO DECIDIO GABRI el 19 de octubre de 2026: *«competencias y
  // ese esquema deberian ser la fila 2 de la plataforma»*. Las cinco tarjetas y «Como se
  // estudia aqui» son una sola fila con un solo fondo, asi que el `<section>`, el tono y el
  // ancho los pone quien las junta —`Landing`— y aqui queda solo el contenido. Dos secciones
  // pegadas con el mismo tono no son una fila: son dos filas sin frontera.
  return (
    <div data-testid="landing-competencias">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <div className="font-mono-alpha text-[10px] uppercase tracking-[0.22em] text-alpha-red">
          Cinco competencias
        </div>
        {/* LA LINEA DEL BOE YA NO ENUMERA, y lo cambio Gabri el 20 de octubre de 2026:
            *«se trabajan con las materias del curriculum oficial (BOE/LOMLOE)»*. La lista
            entera de materias ocupaba dos renglones aqui arriba y volvia a decirse, materia
            a materia, en las cinco tarjetas de debajo. Decir dos veces lo mismo en la misma
            fila no es insistir: es que la segunda vez ya no se lee. */}
        <div className="text-[13px] text-alpha-black/45 max-w-[640px]">
          Se trabajan con las materias del currículum oficial (BOE/LOMLOE).
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {PILLARS.map((p, i) => {
          const color = colorDeLaCompetencia(p.key);
          return (
            <Link
              key={p.key}
              to={`/cursos/${p.key}`}
              data-testid={`hero-pilar-${p.key}`}
              aria-label={`Abrir la competencia ${p.label}`}
              {...pistasDe("curso", TRAE_CURSO)}
              /* `overflow-hidden` NO ES ADORNO: sin el, la banda sale cuadrada por arriba y
                 se come las dos esquinas redondeadas. Es la misma nota que la tarjeta de
                 `/cursos`, y por el mismo motivo. */
              className={`hilo-de-luz group rounded-caja flex flex-col h-full overflow-hidden border
                          bg-white transition-all duration-200 hover:-translate-y-1.5 hover:shadow-2xl
                          border-alpha-black/15 ${color.bordeAlPasar} ${color.luzAlPasar}
                          focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-alpha-black focus-visible:ring-offset-2`}
            >
              <div className={`h-2 shrink-0 transition-colors ${color.suave} ${color.bandaAlPasar}`} />
              <div className="p-5 flex flex-col h-full">
                <div className="flex items-start justify-between gap-2">
                  {/* EL EMOJI DE SU MATERIA, y lo busca por el nombre de la familia que
                      declara `PILLARS`. Si alguien renombra esa familia, `emojiDe` devuelve
                      cadena vacia y la tarjeta sale sin emoji: se nota, y lo canta el
                      portero de la portada antes de que se note. */}
                  <span className="text-[40px] leading-none select-none" aria-hidden="true">
                    {emojiDe(p.familia)}
                  </span>
                  <span className={`rounded-full shrink-0 px-2.5 py-1 font-mono-alpha
                                    text-[10px] uppercase tracking-widest text-white ${color.banda}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-auto pt-6">
                  {/* ONCE PÍXELES Y NO DIEZ, y la mitad de interletrado: una evaluación
                      externa del 15 de septiembre de 2026 transcribió «CULTURA Y
                      CIVELIZACIÓN» de una captura de esta píldora. A ese tamaño, separar las
                      letras deja de ayudar a leer. Aquí el nombre pasa además a la serif y a
                      23px, que es lo que de verdad lo arregla. */}
                  <span className={`block font-serif-display text-[23px] leading-[1.08]
                                    transition-colors ${color.textoAlPasar}`}>
                    {p.label}
                  </span>
                  {/* LAS MATERIAS, EN UNA SOLA LINEA. `truncate` no es una red de seguridad
                      decorativa: es lo que impide que una materia nueva parta el subtitulo en
                      dos y descuadre la fila entera, que es justo lo que vio Gabri en
                      Comunicacion. Si no cabe, se corta; no se parte. */}
                  <span className="block text-[12px] text-alpha-black/45 mt-1.5 truncate">
                    {p.asignaturas.join(" · ")}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
